import React, { useMemo, useState } from "react";
import { Button, Img, Heading, Text } from "./..";
import { formatPrice } from "utils/formatPrice";

/** helpers */
const pad2 = (n) => String(n).padStart(2, "0");
const formatDayLabel = (dateLike) => {
  if (!dateLike) return "-";
  const d = new Date(dateLike);
  if (isNaN(d)) return "-";
  return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()}`;
};
const formatShortDate = (dateLike) => {
  if (!dateLike) return "-";
  const d = new Date(dateLike);
  if (isNaN(d)) return "-";
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${String(d.getFullYear()).slice(-2)}`;
};

const pick = (obj, paths, fallback = "-") => {
  for (const p of paths) {
    const parts = p.split(".");
    let cur = obj;
    let ok = true;
    for (const k of parts) {
      if (cur && Object.prototype.hasOwnProperty.call(cur, k)) cur = cur[k];
      else { ok = false; break; }
    }
    if (ok && cur !== null && cur !== undefined && cur !== "") return cur;
  }
  return fallback;
};

const NOTIF_COLS = [
  { key: "msgStatus",     title: "נצפה",               w: "6%"  },
  { key: "type",          title: "סוג",                w: "12%" },
  { key: "model",         title: "דגם הרכב",           w: "14%" },
  { key: "year",          title: "שנת יצור",           w: "7%"  },
  { key: "date",          title: "תאריך",              w: "9%"  },
  { key: "myOffer",       title: "מחיר הצעה שלי",      w: "12%" },
  { key: "approvedOffer", title: "מחיר הצעה שאושרה",   w: "12%" },
  { key: "minPrice",      title: "מחיר מומלץ",       w: "12%" },
  { key: "actions",       title: "פעולה",              w: "16%" },
];

const parseNumber = (v) => {
  if (v === null || v === undefined) return null;
  const s = String(v).replace(/[^\d.-]/g, "");
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

const formatMoney = (val) => {
  if (val === null || val === undefined || val === "" || val === "-") return "---";
  const s = String(val).trim();
  if (!s) return "---";
  if (s.includes("-")) {
    const [a, b] = s.split("-").map((x) => x.trim());
    const na = parseNumber(a);
    const nb = parseNumber(b);
    if (na !== null && nb !== null) return String.fromCharCode(0x20AA) + formatPrice(na) + " - " + String.fromCharCode(0x20AA) + formatPrice(nb);
    return s;
  }
  const n = parseNumber(s);
  return n !== null ? String.fromCharCode(0x20AA) + formatPrice(n) : s;
};

const getMyOfferVal = (n) =>
  pick(n, ["my_offer_price","my_price","offered_price","offer_price","data.my_offer_price","data.offered_price"], null);

const getApprovedOfferVal = (n) => {
  const rawType = String(pick(n, ["type", "notification_type", "type_text"], "")).toLowerCase();
  const isRejected = rawType.includes("offer_approved_other");
  if (isRejected) {
    return pick(n, ["approved_other_price","winner_offer_price","approved_offer_price","approved_price","accepted_price","data.approved_other_price","data.winner_offer_price","data.approved_price"], null);
  }
  return pick(n, ["approved_offer_price","approved_price","accepted_price","winner_offer_price","data.approved_offer_price","data.approved_price","data.accepted_price","my_offer_price","offered_price","offer_price","data.my_offer_price","data.offered_price"], null);
};

const getMinPriceVal = (n) =>
  pick(n, ["asking_price","min_price","minimum_price","car.asking_price","data.asking_price","data.min_price"], null);

export default function NotificationsTable({ dayGroups = [], onMarkRead, onOpen }) {
  const template = NOTIF_COLS.map((c) => c.w).join(" ");

  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("desc");
  const [localReadIds, setLocalReadIds] = useState(new Set());

  const markRead = (n) => {
    const id = n && n.id;
    if (id) setLocalReadIds((prev) => { const s = new Set(prev); s.add(id); return s; });
    if (onMarkRead) onMarkRead(n);
  };

  const notifTypeMap = {
    approved: { txt: "הצעה מאושרת", clr: "rgb(119, 172, 59)", bg: "rgb(237, 255, 216)" },
    rejected: { txt: "הצעה נדחתה",  clr: "rgb(48, 49, 49)",   bg: "rgb(233, 233, 233)" },
    immediatePurchase: { txt: "רכישה מידית", clr: "rgb(119, 172, 59)", bg: "rgb(237, 255, 216)" },
    immediatePurchaseClosedOther: { txt: "סוחר אחר רכש את הרכב", clr: "rgb(214, 31, 31)", bg: "rgb(255, 233, 233)" },
  };

  const getNotifType = (n) => {
    const approvedFlag = Number(pick(n, ["offer_approved", "data.offer_approved"], 0)) === 1;
    const rejectedFlag = Number(pick(n, ["offer_approved_other", "data.offer_approved_other"], 0)) === 1;
    const rawType = String(pick(n, ["type","notification_type","type_text","title","message"], "")).toLowerCase();
    const isImmediatePurchase = rawType === "immediate_purchase_approved" || rawType.includes("immediate_purchase_approved");
    const isImmediatePurchaseClosedOther = rawType === "immediate_purchase_closed_other" || rawType.includes("immediate_purchase_closed_other");
    const isRejected = rejectedFlag || rawType === "offer_approved_other" || rawType.includes("offer_approved_other");
    const isApproved = approvedFlag || rawType === "offer_approved" || (rawType.includes("offer_approved") && !rawType.includes("offer_approved_other"));
    if (isImmediatePurchaseClosedOther) return Object.assign({}, notifTypeMap.immediatePurchaseClosedOther, { key: "immediate_purchase_closed_other" });
    if (isImmediatePurchase) return Object.assign({}, notifTypeMap.immediatePurchase, { key: "immediate_purchase" });
    if (isApproved) return Object.assign({}, notifTypeMap.approved, { key: "approved" });
    if (isRejected) return Object.assign({}, notifTypeMap.rejected, { key: "rejected" });
    const fallbackTxt = pick(n, ["title","type_text","type","notification_type","message"], "-");
    return { txt: fallbackTxt, clr: "#666", bg: "transparent", key: "other" };
  };

  const getSortValue = (n, key) => {
    const createdAt = pick(n, ["created_at","creation_date","date","createdAt"], null);
    switch (key) {
      case "type":         return String(getNotifType(n).txt || "").toLowerCase();
      case "model":        return String(pick(n, ["car.model","model","car_model","data.model"], "")).toLowerCase();
      case "myOffer":      return parseNumber(getMyOfferVal(n));
      case "approvedOffer":return parseNumber(getApprovedOfferVal(n));
      case "minPrice":     return parseNumber(getMinPriceVal(n));
      case "year":         return parseNumber(pick(n, ["car.year","year","car_year","data.year"], null));
      case "date":         return createdAt ? new Date(createdAt).getTime() : null;
      case "offer":        return parseNumber(pick(n, ["offered_price","offer_price","price","data.offered_price"], null));
      case "saleType":     return String(pick(n, ["sale_type","sale_type_text","sale_type_id","data.sale_type_id"], "")).toLowerCase();
      case "msgStatus":    return Number(pick(n, ["is_read","read","data.is_read"], 0));
      default:             return "";
    }
  };

  const compareValues = (a, b) => {
    const aNull = a === null || a === undefined || a === "";
    const bNull = b === null || b === undefined || b === "";
    if (aNull && bNull) return 0;
    if (aNull) return 1;
    if (bNull) return -1;
    if (typeof a === "number" && typeof b === "number") return a - b;
    return String(a).localeCompare(String(b), "he", { numeric: true, sensitivity: "base" });
  };

  const sortedDayGroups = useMemo(() => {
    if (!sortKey) return dayGroups;
    const flat = dayGroups.flatMap((g) => g.items.map((n) => ({ dayLabel: g.dayLabel, n })));
    flat.sort((a, b) => {
      const diff = compareValues(getSortValue(a.n, sortKey), getSortValue(b.n, sortKey));
      return sortDir === "asc" ? diff : -diff;
    });
    const map = new Map();
    flat.forEach(({ dayLabel, n }) => {
      if (!map.has(dayLabel)) map.set(dayLabel, []);
      map.get(dayLabel).push(n);
    });
    return Array.from(map.entries()).map(([dayLabel, items]) => ({ dayLabel, items }));
  }, [dayGroups, sortKey, sortDir]);

  const SeenBadge = ({ isRead, onClick }) => {
    const bg  = isRead ? "rgb(237, 255, 216)" : "rgb(255, 233, 233)";
    const clr = isRead ? "rgb(119, 172, 59)"  : "rgb(214, 31, 31)";
    return (
      React.createElement("button", {
        type: "button", onClick,
        className: "w-[30px] h-[30px] rounded-[8px] flex items-center justify-center cursor-pointer",
        style: { backgroundColor: bg },
        title: isRead ? "נצפה" : "לא נצפה"
      },
        React.createElement("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none" },
          React.createElement("path", { d: "M2.5 12s3.5-7 9.5-7 9.5 7 9.5 7-3.5 7-9.5 7-9.5-7-9.5-7Z", stroke: clr, strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }),
          React.createElement("circle", { cx: "12", cy: "12", r: "2.5", stroke: clr, strokeWidth: "2" })
        )
      )
    );
  };

  const sortAsc  = (key) => { setSortKey(key); setSortDir("asc");  };
  const sortDesc = (key) => { setSortKey(key); setSortDir("desc"); };

  return (
    <div dir="rtl" className="w-full overflow-x-auto">
      <div className="grid items-center bg-white sticky top-0 z-10" style={{ gridTemplateColumns: template, borderBottom: "1px solid #E9E9E9" }}>
        {NOTIF_COLS.map((c) => (
          <div key={c.key} className="flex items-center justify-center py-2 text-sm font-assistant font-bold gap-2 select-none">
            <span>{c.title}</span>
            <div style={{ width: "8px" }} className="flex flex-col items-center">
              <button type="button" onClick={() => sortAsc(c.key)} className="leading-none">
                <img src="/images/img_vector_red_a700_03_9.svg" alt="up" className="mb-0.5" style={{ opacity: sortKey === c.key && sortDir === "asc" ? 1 : 0.45 }} />
              </button>
              <button type="button" onClick={() => sortDesc(c.key)} className="leading-none">
                <img src="/images/img_vector_red_a700_03_10.svg" alt="down" className="mt-0.5" style={{ opacity: sortKey === c.key && sortDir === "desc" ? 1 : 0.45 }} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {sortedDayGroups.map((g) => (
        <div key={g.dayLabel} className="w-full">
          <div className="flex justify-start pr-2 pt-5 pb-2 text-[13px] text-gray-500 font-heebo">{g.dayLabel}</div>
          {g.items.map((n) => {
            const createdAt = pick(n, ["created_at","creation_date","date","createdAt"], null);
            const isRead = localReadIds.has(n && n.id) || Number(pick(n, ["is_read","read","data.is_read"], 0)) === 1;
            const model = pick(n, ["car.model","model","car_model","data.model"], "-");
            const year  = pick(n, ["car.year","year","car_year","data.year"], "-");
            return (
              <div
                key={n.id != null ? n.id : g.dayLabel + Math.random()}
                className="grid items-center gap-0 text-[15px]"
                style={{ gridTemplateColumns: template, borderBottom: "1px solid #E9E9E9", height: "4rem", margin: "0.5rem 0", opacity: isRead ? 0.8 : 1 }}
              >
                <span className="flex justify-center items-center">
                  <SeenBadge isRead={isRead} onClick={() => { if (!isRead) markRead(n); }} />
                </span>
                <span className="flex justify-center items-center text-center px-1">
                  {(() => {
                    const t = getNotifType(n);
                    if (t.key === "approved" || t.key === "rejected" || t.key === "immediate_purchase" || t.key === "immediate_purchase_closed_other") {
                      return (
                        <Button size="lg" shape="round" style={{ backgroundColor: t.bg, color: t.clr, fontSize: "12px", height: "auto" }} className="px-3 py-1 rounded-[5px] text-[12px] font-semibold leading-none">
                          {t.txt}
                        </Button>
                      );
                    }
                    return <span className="text-[13px] text-gray-700">{t.txt}</span>;
                  })()}
                </span>
                <span className="flex justify-center items-center text-center">{model}</span>
                <span className="flex justify-center">{year}</span>
                <span className="flex justify-center text-center">{formatShortDate(createdAt)}</span>
                <span className="flex justify-center items-center text-center">
                  <Heading className="!font-heebo text-[14px]" style={{ fontWeight: "400" }}>{formatMoney(getMyOfferVal(n))}</Heading>
                </span>
                <span className="flex justify-center items-center text-center">
                  <Heading className="!font-heebo text-[14px]" style={{ fontWeight: "400" }}>{formatMoney(getApprovedOfferVal(n))}</Heading>
                </span>
                <span className="flex justify-center items-center text-center">
                  <Heading className="!font-heebo text-[14px]" style={{ fontWeight: "400" }}>{formatMoney(getMinPriceVal(n))}</Heading>
                </span>
                <span className="flex justify-center items-center gap-2">
                  <span onClick={() => { if (!isRead) markRead(n); if (onOpen) onOpen(n); }} className="flex items-center cursor-pointer">
                    <h6 className="text-black-900_03 font-assistant text-[16px] font-semibold lg:text-[13px]">לצפיה במודעה</h6>
                    <img className="h-[14px] ml-2 mr-2" src="/images/img_settings_black_900.svg" alt="Settings" />
                  </span>
                </span>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
